/**
 * Custom CKEditor 5 build.
 *
 * This is a from-source equivalent of @ckeditor/ckeditor5-build-classic 40.2.0
 * (same plugins, same toolbar) with one addition: the GeneralHtmlSupport (GHS)
 * plugin. GHS makes the editor preserve arbitrary HTML instead of stripping
 * every tag/attribute its schema does not explicitly know. Without it, opening
 * a forwarded/replied mail body in the editor silently dropped large parts of
 * the original message (Outlook markup, nested structures, extra list items).
 *
 * Building from source requires the webpack tweaks in craco.config.js.
 */
import {ClassicEditor as ClassicEditorBase} from "@ckeditor/ckeditor5-editor-classic";

import {Essentials} from "@ckeditor/ckeditor5-essentials";
import {UploadAdapter} from "@ckeditor/ckeditor5-adapter-ckfinder";
import {Autoformat} from "@ckeditor/ckeditor5-autoformat";
import {Bold, Italic} from "@ckeditor/ckeditor5-basic-styles";
import {BlockQuote} from "@ckeditor/ckeditor5-block-quote";
import {CKBox} from "@ckeditor/ckeditor5-ckbox";
import {CKFinder} from "@ckeditor/ckeditor5-ckfinder";
import {EasyImage} from "@ckeditor/ckeditor5-easy-image";
import {Heading} from "@ckeditor/ckeditor5-heading";
import {
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	PictureEditing,
} from "@ckeditor/ckeditor5-image";
import {Indent} from "@ckeditor/ckeditor5-indent";
import {Link} from "@ckeditor/ckeditor5-link";
import {List} from "@ckeditor/ckeditor5-list";
import {MediaEmbed} from "@ckeditor/ckeditor5-media-embed";
import {Paragraph} from "@ckeditor/ckeditor5-paragraph";
import {PasteFromOffice} from "@ckeditor/ckeditor5-paste-from-office";
import {
	Table,
	TableToolbar,
} from "@ckeditor/ckeditor5-table";
import {TextTransformation} from "@ckeditor/ckeditor5-typing";
import {CloudServices} from "@ckeditor/ckeditor5-cloud-services";

// The reason this custom build exists:
import {GeneralHtmlSupport} from "@ckeditor/ckeditor5-html-support";

export class ClassicEditor extends ClassicEditorBase {}

// Plugins to include in the build. Mirrors the stock classic build 40.2.0 and
// appends GeneralHtmlSupport.
ClassicEditor.builtinPlugins = [
	Essentials,
	UploadAdapter,
	Autoformat,
	Bold,
	Italic,
	BlockQuote,
	CKBox,
	CKFinder,
	CloudServices,
	EasyImage,
	Heading,
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	Link,
	List,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	PictureEditing,
	Table,
	TableToolbar,
	TextTransformation,
	GeneralHtmlSupport,
];

// Editor configuration. Toolbar/image/table match the stock classic build.
// The htmlSupport section is what actually preserves the incoming mail markup:
// allow every element with all of its attributes, classes and styles.
ClassicEditor.defaultConfig = {
	toolbar: {
		items: [
			"undo",
			"redo",
			"|",
			"heading",
			"|",
			"bold",
			"italic",
			"|",
			"link",
			"uploadImage",
			"insertTable",
			"blockQuote",
			"mediaEmbed",
			"|",
			"bulletedList",
			"numberedList",
			"outdent",
			"indent",
		],
	},
	image: {
		toolbar: [
			"imageStyle:inline",
			"imageStyle:block",
			"imageStyle:side",
			"|",
			"toggleImageCaption",
			"imageTextAlternative",
		],
	},
	table: {
		contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
	},
	htmlSupport: {
		allow: [
			{
				name: /.*/,
				attributes: true,
				classes: true,
				styles: true,
			},
		],
	},
	language: "en",
};

export default ClassicEditor;
